import { Link } from "react-router-dom";
import { ArrowRight, Users2, Briefcase, Home as HomeIcon, TrendingUp, FileSearch, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { SectionHeading, Reveal, ProcessTimeline, CTASection, CheckItem, TrustBar } from "@/components/shared/Shared";
import { useContent } from "@/context/ContentContext";

const IMG = {
  familie: "https://images.unsplash.com/photo-1511895426328-dc8714191300?q=80&w=1400&auto=format&fit=crop",
};

export default function Privatkunden() {
  const { t } = useLang();
  const hero = useContent("privatkunden.hero");

  const groups = [
    { icon: Users2, label: t("Familien", "Families") },
    { icon: Briefcase, label: t("Arbeitnehmer", "Employees") },
    { icon: TrendingUp, label: t("Selbstst\u00e4ndige & Unternehmer", "Self-employed & entrepreneurs") },
    { icon: HomeIcon, label: t("Immobilieninteressenten", "Real estate prospects") },
    { icon: FileSearch, label: t("Personen mit bestehenden Vertr\u00e4gen", "People with existing contracts") },
    { icon: HeartHandshake, label: t("Kapitalanleger", "Investors") },
  ];

  const steps = [
    { title: t("Erstgespr\u00e4ch", "Initial meeting"), text: t("Kostenfrei und unverbindlich: Wir lernen Ihre Situation und Ziele kennen.", "Free and non-binding: we get to know your situation and goals.") },
    { title: t("Bestandsaufnahme", "Inventory"), text: t("Bestehende Vertr\u00e4ge und Ihre Ausgangslage werden strukturiert erfasst.", "Existing contracts and your starting position are recorded in a structured way.") },
    { title: t("Einordnung & Empfehlung", "Assessment & recommendation"), text: t("Sie erhalten eine verst\u00e4ndliche, dokumentierte Empfehlung mit transparenten Kosten.", "You receive a clear, documented recommendation with transparent costs.") },
    { title: t("Begleitung", "Support"), text: t("Wir begleiten die Umsetzung und bleiben langfristig Ihr Ansprechpartner.", "We support implementation and remain your long-term contact.") },
  ];

  return (
    <div>
      <Seo
        title={t("Privatkunden \u2013 Beratung f\u00fcr jede Lebenssituation", "Private Clients \u2013 Advice for Every Life Situation")}
        description={t(
          "Finanzberatung f\u00fcr Familien, Arbeitnehmer, Selbstst\u00e4ndige und Kapitalanleger: Ziele verstehen, Vertr\u00e4ge pr\u00fcfen, dokumentierte Empfehlungen erhalten.",
          "Financial advice for families, employees, self-employed and investors: understand goals, review contracts, receive documented recommendations."
        )}
      />
      {/* HERO */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionHeading
                eyebrow={hero.eyebrow || t("Privatkunden", "Private Clients")}
                title={hero.headline || t("Beratung f\u00fcr jede Lebenssituation", "Advice for every life situation")}
                subtitle={hero.subline || t(
                  "Ob Familie, Berufseinstieg, Selbstst\u00e4ndigkeit oder Verm\u00f6gensaufbau \u2013 Eleven Family Office begleitet Sie mit pers\u00f6nlicher Beratung, transparenten Kosten und dokumentierten Prozessen.",
                  "Whether family, career start, self-employment or wealth creation \u2013 Eleven Family Office supports you with personal advice, transparent costs and documented processes."
                )}
              />
              <div className="mt-7 space-y-3">
                <CheckItem>{t("Ihre Ziele stehen am Anfang \u2013 nicht ein Produkt", "Your goals come first \u2013 not a product")}</CheckItem>
                <CheckItem>{t("Bestehende Vertr\u00e4ge werden verst\u00e4ndlich eingeordnet", "Existing contracts are clearly explained")}</CheckItem>
                <CheckItem>{t("Jede Empfehlung wird dokumentiert", "Every recommendation is documented")}</CheckItem>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="privatkunden-cta">
                  <Link to="/anfrage/rueckruf">{t("Beratung anfragen", "Request advice")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 hover:border-[#FF3C20]/60" data-testid="privatkunden-berater-cta">
                  <Link to="/berater-finden">{t("Berater finden", "Find an advisor")}</Link>
                </Button>
              </div>
            </div>
            <Reveal>
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-black/5">
                <img src={IMG.familie} alt={t("Familie", "Family")} className="w-full h-[420px] object-cover" loading="lazy" decoding="async" />
              </div>
            </Reveal>
          </div>
        </div>
        <TrustBar />
      </section>

      {/* TARGET GROUPS */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={t("F\u00fcr wen", "For whom")}
            title={t("F\u00fcr wen ist Eleven Family Office geeignet?", "Who is Eleven Family Office suitable for?")}
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="privatkunden-groups">
            {groups.map((g) => (
              <Reveal key={g.label} className="rounded-[8px] bg-white border border-border p-6 card-hover">
                <g.icon className="h-5 w-5 text-[#E8241B]" />
                <p className="mt-3.5 text-sm font-semibold text-[#111318]">{g.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("Ablauf", "Process")} title={t("So l\u00e4uft die Beratung ab", "How the advice process works")} />
          <div className="mt-12"><ProcessTimeline steps={steps} /></div>
        </div>
      </section>

      <CTASection
        title={t("Starten Sie mit einem unverbindlichen Gespr\u00e4ch", "Start with a non-binding conversation")}
        subtitle={t("Wir nehmen uns Zeit f\u00fcr Ihre Situation \u2013 pers\u00f6nlich, verst\u00e4ndlich und ohne Produktdruck.", "We take time for your situation \u2013 personal, clear and without product pressure.")}
        primaryLabel={t("Beratung anfragen", "Request advice")}
        primaryTo="/anfrage/rueckruf"
        secondaryLabel={t("Termin buchen", "Book appointment")}
        secondaryTo="/termin"
      />
    </div>
  );
}
